import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Box, Button, CircularProgress, Typography } from '@mui/material';

// Layout Components
import Header from './components/Layout/Header';
import Footer from './components/Layout/Footer';
import DashboardLayout from './components/Layout/DashboardLayout';
import CookieConsentBanner from './components/Layout/CookieConsentBanner';

// Public Pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/Auth/LoginPage';
import RegisterPage from './pages/Auth/RegisterPage';
import ForgotPasswordPage from './pages/Auth/ForgotPasswordPage';
import VerifyEmailPage from './pages/Auth/VerifyEmailPage';
import AboutPage from './pages/About/AboutPage';
import ContactPage from './pages/Contact/ContactPage';
import HelpPage from './pages/Help/HelpPage';
import PricingPage from './pages/Pricing/PricingPage';

// Visa & Countries
import VisaCheckPage from './pages/Visa/VisaCheckPage';
import VisaDetailPage from './pages/Visa/VisaDetailPage';
import CountriesPage from './pages/Countries/CountriesPage';
import CountryDetailPage from './pages/Countries/CountryDetailPage';
import CountryVisasPage from './pages/Countries/CountryVisasPage';

// Dashboard Pages
import DashboardPage from './pages/Dashboard/DashboardPage';
import AdminDashboard from './pages/Dashboard/AdminDashboard';
import AgentDashboard from './pages/Dashboard/AgentDashboard';
import CorporateDashboard from './pages/Dashboard/CorporateDashboard';
import EnterpriseDashboard from './pages/Dashboard/EnterpriseDashboard';

// Application Pages
import ApplicationPage from './pages/Application/ApplicationPage';
import ApplicationDetailPage from './pages/Application/ApplicationDetailPage';
import ApplicationsListPage from './pages/Application/ApplicationsListPage';
import NewApplicationPage from './pages/Application/NewApplicationPage';

// Account Pages
import PaymentPage from './pages/Payment/PaymentPage';
import ProfilePage from './pages/Profile/ProfilePage';
import SettingsPage from './pages/Settings/SettingsPage';
import CheckoutSuccessPage from './pages/Checkout/CheckoutSuccessPage';

// Context
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';

// Utils
import ProtectedRoute from './components/Auth/ProtectedRoute';
import { captureAttributionFromUrl } from './lib/attribution';

// ─── Lazy-loaded Pages (Admin) ──────────────────────────────────────────────
const AdminApplicationsPage = React.lazy(() => import('./pages/Admin/AdminApplicationsPage'));
const AdminUsersPage = React.lazy(() => import('./pages/Admin/AdminUsersPage'));
const AdminReportsPage = React.lazy(() => import('./pages/Admin/AdminReportsPage'));
const AdminHealthPage = React.lazy(() => import('./pages/Admin/AdminHealthPage'));
const AdminFeaturesPage = React.lazy(() => import('./pages/Admin/AdminFeaturesPage'));
const AdminLogsPage = React.lazy(() => import('./pages/Admin/AdminLogsPage'));
const AdminSupportPage = React.lazy(() => import('./pages/Admin/AdminSupportPage'));
const AdminSettingsPage = React.lazy(() => import('./pages/Admin/AdminSettingsPage'));
const AdminAnalyticsPage = React.lazy(() => import('./pages/Admin/AdminAnalyticsPage'));
const AdminBulkOpsPage = React.lazy(() => import('./pages/Admin/AdminBulkOpsPage'));
const AdminAuditPage = React.lazy(() => import('./pages/Admin/AdminAuditPage'));
const AdminEmailTemplatesPage = React.lazy(() => import('./pages/Admin/AdminEmailTemplatesPage'));
const AdminCreditsPage = React.lazy(() => import('./pages/Admin/AdminCreditsPage'));

// ─── Lazy-loaded Pages (Agent) ──────────────────────────────────────────────
// AgentDashboardPage lazy-loaded below if needed
const AgentClientsPage = React.lazy(() => import('./pages/Agent/AgentClientsPage'));
const AgentClientDetailPage = React.lazy(() => import('./pages/Agent/AgentClientDetailPage'));
const AgentNewClientPage = React.lazy(() => import('./pages/Agent/AgentNewClientPage'));
const AgentApplicationsPage = React.lazy(() => import('./pages/Agent/AgentApplicationsPage'));
const AgentCommissionPage = React.lazy(() => import('./pages/Agent/AgentCommissionPage'));
const AgentCommissionReportsPage = React.lazy(() => import('./pages/Agent/AgentCommissionReportsPage'));
const AgentClientPortalPage = React.lazy(() => import('./pages/Agent/AgentClientPortalPage'));
const AgentPayoutsPage = React.lazy(() => import('./pages/Agent/AgentPayoutsPage'));
const AgentResourcesPage = React.lazy(() => import('./pages/Agent/AgentResourcesPage'));
const AgentSettingsPage = React.lazy(() => import('./pages/Agent/AgentSettingsPage'));
const AgentBulkSubmitPage = React.lazy(() => import('./pages/Agent/AgentBulkSubmitPage'));
const AgentReportsPage = React.lazy(() => import('./pages/Agent/AgentReportsPage'));

// ─── Lazy-loaded Pages (Corporate) ─────────────────────────────────────────
const CorporateEmployeesPage = React.lazy(() => import('./pages/Corporate/CorporateEmployeesPage'));
const CorporateDepartmentsPage = React.lazy(() => import('./pages/Corporate/CorporateDepartmentsPage'));
const CorporateBulkPage = React.lazy(() => import('./pages/Corporate/CorporateBulkPage'));
const CorporateApprovalsPage = React.lazy(() => import('./pages/Corporate/CorporateApprovalsPage'));
const CorporatePoliciesPage = React.lazy(() => import('./pages/Corporate/CorporatePoliciesPage'));
const CorporateBudgetsPage = React.lazy(() => import('./pages/Corporate/CorporateBudgetsPage'));
const CorporateReportsPage = React.lazy(() => import('./pages/Corporate/CorporateReportsPage'));

// ─── Lazy-loaded Pages (Enterprise) ────────────────────────────────────────
const EnterpriseSSOPage = React.lazy(() => import('./pages/Enterprise/EnterpriseSSOPage'));
const EnterpriseCompliancePage = React.lazy(() => import('./pages/Enterprise/EnterpriseCompliancePage'));
const EnterpriseSandboxPage = React.lazy(() => import('./pages/Enterprise/EnterpriseSandboxPage'));
const EnterpriseTeamPage = React.lazy(() => import('./pages/Enterprise/EnterpriseTeamPage'));
const EnterpriseBudgetPage = React.lazy(() => import('./pages/Enterprise/EnterpriseBudgetPage'));
const EnterpriseTravelPage = React.lazy(() => import('./pages/Enterprise/EnterpriseTravelPage'));
const EnterpriseEntitiesPage = React.lazy(() => import('./pages/Enterprise/EnterpriseEntitiesPage'));
const EnterpriseWorkforcePage = React.lazy(() => import('./pages/Enterprise/EnterpriseWorkforcePage'));
const EnterpriseAPIPage = React.lazy(() => import('./pages/Enterprise/EnterpriseAPIPage'));
const EnterpriseWhitelabelPage = React.lazy(() => import('./pages/Enterprise/EnterpriseWhitelabelPage'));
const EnterpriseWorkflowsPage = React.lazy(() => import('./pages/Enterprise/EnterpriseWorkflowsPage'));
const EnterpriseAnalyticsPage = React.lazy(() => import('./pages/Enterprise/EnterpriseAnalyticsPage'));

// ─── Lazy-loaded Pages (Credits) ────────────────────────────────────────────
const CreditsPage = React.lazy(() => import('./pages/Credits/CreditsPage'));
const CreditsPurchasePage = React.lazy(() => import('./pages/Credits/CreditsPurchasePage'));
const CreditsSuccessPage = React.lazy(() => import('./pages/Credits/CreditsSuccessPage'));
const CreditsHistoryPage = React.lazy(() => import('./pages/Credits/CreditsHistoryPage'));
const CreditsPromoPage = React.lazy(() => import('./pages/Credits/CreditsPromoPage'));

// ─── Lazy-loaded Pages (Travel) ─────────────────────────────────────────────
const TravelSearchPage = React.lazy(() => import('./pages/Travel/TravelSearchPage'));
const TravelItineraryPage = React.lazy(() => import('./pages/Travel/TravelItineraryPage'));
const TravelHistoryPage = React.lazy(() => import('./pages/Travel/TravelHistoryPage'));
const TravelIntelPage = React.lazy(() => import('./pages/Travel/TravelIntelPage'));

// ─── Lazy-loaded Pages (Documents) ──────────────────────────────────────────
const DocumentsPage = React.lazy(() => import('./pages/Documents/DocumentsPage'));
const DocumentsUploadPage = React.lazy(() => import('./pages/Documents/DocumentsUploadPage'));
const DocumentsChecklistPage = React.lazy(() => import('./pages/Documents/DocumentsChecklistPage'));

// ─── Lazy-loaded Pages (Misc) ───────────────────────────────────────────────
const NotificationsPage = React.lazy(() => import('./pages/Notifications/NotificationsPage'));
const DraftsPage = React.lazy(() => import('./pages/Applications/DraftsPage'));
const SubscriptionsPage = React.lazy(() => import('./pages/Subscriptions/SubscriptionsPage'));
const AIAdvisorPage = React.lazy(() => import('./pages/AIAdvisor/AIAdvisorPage'));
const IntelligencePage = React.lazy(() => import('./pages/Intelligence/IntelligencePage'));
const VerificationPage = React.lazy(() => import('./pages/Verification/VerificationPage'));
const BillingPage = React.lazy(() => import('./pages/Billing/BillingPage'));
const InvoicesPage = React.lazy(() => import('./pages/Billing/InvoicesPage'));
const BulkUploadPage = React.lazy(() => import('./pages/BulkUpload/BulkUploadPage'));
const RemindersPage = React.lazy(() => import('./pages/Reminders/RemindersPage'));
const SavedPage = React.lazy(() => import('./pages/Saved/SavedPage'));
const CountryHubPage = React.lazy(() => import('./pages/CountryHub/CountryHubPage'));
const InterviewPrepPage = React.lazy(() => import('./pages/InterviewPrep/InterviewPrepPage'));
const CompliancePage = React.lazy(() => import('./pages/Compliance/CompliancePage'));

// ─── Lazy-loaded Pages (Static) ─────────────────────────────────────────────
const CareersPage = React.lazy(() => import('./pages/Static/CareersPage'));
const ChatPage = React.lazy(() => import('./pages/Static/ChatPage'));
const ConsultationPage = React.lazy(() => import('./pages/Static/ConsultationPage'));
const CookiesPage = React.lazy(() => import('./pages/Static/CookiesPage'));
const FAQPage = React.lazy(() => import('./pages/Static/FAQPage'));
const PartnersPage = React.lazy(() => import('./pages/Static/PartnersPage'));
const PressPage = React.lazy(() => import('./pages/Static/PressPage'));
const PrivacyPage = React.lazy(() => import('./pages/Static/PrivacyPage'));
const TermsPage = React.lazy(() => import('./pages/Static/TermsPage'));
const DeleteAccountPage = React.lazy(() => import('./pages/Static/DeleteAccountPage'));

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

// Create Material-UI theme — Tripnovac Visa Platform
const theme = createTheme({
  palette: {
    mode: 'dark', // Dark premium theme — matches Tripnovac brand
    primary: {
      main: '#1273E5', // Tripnovac primary blue
      light: '#42a5f5',
      dark: '#0958A5',
    },
    secondary: {
      main: '#00BCEC', // Tripnovac secondary (gradient complement)
      light: '#4DD0F0',
      dark: '#0095BB',
    },
    success: {
      main: '#29B848', // Green success
      light: '#4CAF50',
      dark: '#1B7A32',
    },
    error: {
      main: '#f44336',
      light: '#ff6659',
      dark: '#d32f2f',
    },
    warning: {
      main: '#ff9800',
      light: '#ffb74d',
      dark: '#f57c00',
    },
    info: {
      main: '#1273E5',
      light: '#42a5f5',
      dark: '#0958A5',
    },
    background: {
      default: '#030B1B', // Deep dark (Tripnovac brand)
      paper: '#0F172A', // Dark panels/cards
    },
    text: {
      primary: '#FFFFFF', // White text on dark backgrounds
      secondary: 'rgba(255, 255, 255, 0.7)', // Muted light text
    },
    grey: {
      100: '#1E293B',
      200: '#334155',
      300: '#475569',
      400: '#64748B',
      500: '#94A3B8',
      600: '#CBD5E1',
      700: '#E2E8F0',
      800: '#F1F5F9',
      900: '#F8FAFC',
    },
    divider: 'rgba(255, 255, 255, 0.12)',
  },
  typography: {
    fontFamily: '"Inter", "Instrument Sans", "Roboto", "Helvetica", "Arial", sans-serif', // Modern font stack
    h1: {
      fontSize: '2.75rem', // 44px (from Tripnovac logo size)
      fontWeight: 600,
      letterSpacing: '0.01em',
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2rem', // 32px
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.5rem', // 24px (from Tripnovac headings)
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.25rem', // 20px
      fontWeight: 500,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.125rem', // 18px
      fontWeight: 500,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1rem', // 16px
      fontWeight: 600,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem', // 16px (from Tripnovac body text)
      fontWeight: 400,
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem', // 14px
      fontWeight: 400,
      lineHeight: 1.43,
    },
    button: {
      fontSize: '1rem', // 16px
      fontWeight: 500,
      textTransform: 'none' as const,
      lineHeight: 1.5,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.43,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          padding: '10px 24px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
  },
});

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 2,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Loading spinner for React.lazy pages
const LazyLoading: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <CircularProgress />
  </Box>
);

// Auth pages get full-bleed layout (no Header/Footer)
const AuthLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <>{children}</>
);

// Standard pages get Header + Footer
const MainLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#030B1B' }}>
    <Header />
    <main style={{ flex: 1, paddingTop: '80px' }}>
      {children}
    </main>
    <Footer />
  </div>
);

// Authenticated pages get Sidebar + Custom Header (DashboardLayout)
const AuthenticatedLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <DashboardLayout>{children}</DashboardLayout>
);

// Helper: Wrap lazy component in Suspense + Authenticated layout + ProtectedRoute
const LazyAuth: React.FC<{ children: React.ReactNode; role?: 'general' | 'corporate' | 'admin' | 'enterprise' | 'agent' }> = ({ children, role }) => (
  <AuthenticatedLayout>
    <ProtectedRoute requireRole={role}>
      <Suspense fallback={<LazyLoading />}>
        {children}
      </Suspense>
    </ProtectedRoute>
  </AuthenticatedLayout>
);

// Helper: Wrap lazy component in Suspense + MainLayout (public)
const LazyPublic: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <MainLayout>
    <Suspense fallback={<LazyLoading />}>
      {children}
    </Suspense>
  </MainLayout>
);

// Smart layout: uses sidebar for authenticated users, public header/footer otherwise
const SmartLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  if (user) return <AuthenticatedLayout>{children}</AuthenticatedLayout>;
  return <MainLayout>{children}</MainLayout>;
};

// Smart dashboard: renders the right dashboard for the user's role without redirect hops
const SmartDashboard: React.FC = () => {
  const { user } = useAuth();
  const role = (user?.role || '').toLowerCase() === 'user' ? 'general' : (user?.role || 'general').toLowerCase();
  switch (role) {
    case 'admin': return <AdminDashboard />;
    case 'agent': return <AgentDashboard />;
    case 'corporate': return <CorporateDashboard />;
    case 'enterprise': return <EnterpriseDashboard />;
    default: return <DashboardPage />;
  }
};

// ─── Redirect helpers for param-based legacy routes ─────────────────────────
const RedirectVisaCountry = () => { const { countryCode } = useParams(); return <Navigate to={`/countries/${countryCode}`} replace />; };
const RedirectVisaAppDetail = () => { const { id } = useParams(); return <Navigate to={`/applications/${id}`} replace />; };
const RedirectAppEditLegacy = () => { const { applicationId } = useParams(); return <Navigate to={`/applications/edit/${applicationId}`} replace />; };
const RedirectVisaApplication = () => { const { countryCode } = useParams(); return <Navigate to={`/applications/new?country=${countryCode || ''}`} replace />; };

// Smart /apply route: auth users → /applications/new, guests → /register with redirect
const SmartApplyRedirect: React.FC = () => {
  const { user } = useAuth();
  if (user) {
    return <Navigate to="/applications/new" replace />;
  }
  return <Navigate to="/register?redirect=/applications/new" replace />;
};

function App() {
  useEffect(() => {
    captureAttributionFromUrl();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Elements stripe={stripePromise}>
          <AuthProvider>
            <NotificationProvider>
              <Router>
                <div className="App">
                  <Routes>
                    {/* ═══ Auth Routes — Full-bleed, no Header/Footer ═══ */}
                    <Route path="/login" element={<AuthLayout><LoginPage /></AuthLayout>} />
                    <Route path="/register" element={<AuthLayout><RegisterPage /></AuthLayout>} />
                    <Route path="/signup" element={<Navigate to="/register" replace />} />
                    <Route path="/forgot-password" element={<AuthLayout><ForgotPasswordPage /></AuthLayout>} />
                    <Route path="/verify-email" element={<AuthLayout><VerifyEmailPage /></AuthLayout>} />

                    {/* ═══ Apply Route (Smart redirect based on auth state) ═══ */}
                    <Route path="/apply" element={<SmartApplyRedirect />} />
                    
                    {/* ═══ Public Routes ═══ */}
                    <Route path="/" element={<MainLayout><HomePage /></MainLayout>} />
                    <Route path="/visa-check" element={<SmartLayout><VisaCheckPage /></SmartLayout>} />
                    <Route path="/about" element={<MainLayout><AboutPage /></MainLayout>} />
                    <Route path="/contact" element={<SmartLayout><ContactPage /></SmartLayout>} />
                    <Route path="/help" element={<SmartLayout><HelpPage /></SmartLayout>} />
                    <Route path="/pricing" element={<MainLayout><PricingPage /></MainLayout>} />
                    <Route path="/countries" element={<SmartLayout><CountriesPage /></SmartLayout>} />
                    <Route path="/countries/:countryCode" element={<SmartLayout><CountryDetailPage /></SmartLayout>} />
                    <Route path="/countries/:countryCode/visas" element={<SmartLayout><CountryVisasPage /></SmartLayout>} />
                    <Route path="/countries/:countryCode/visas/:visaId" element={<SmartLayout><VisaDetailPage /></SmartLayout>} />

                    {/* ═══ Static / Public Pages ═══ */}
                    <Route path="/careers" element={<LazyPublic><CareersPage /></LazyPublic>} />
                    <Route path="/chat" element={<LazyPublic><ChatPage /></LazyPublic>} />
                    <Route path="/consultation" element={<LazyPublic><ConsultationPage /></LazyPublic>} />
                    <Route path="/cookies" element={<LazyPublic><CookiesPage /></LazyPublic>} />
                    <Route path="/faq" element={<LazyPublic><FAQPage /></LazyPublic>} />
                    <Route path="/partners" element={<LazyPublic><PartnersPage /></LazyPublic>} />
                    <Route path="/press" element={<LazyPublic><PressPage /></LazyPublic>} />
                    <Route path="/privacy" element={<LazyPublic><PrivacyPage /></LazyPublic>} />
                    <Route path="/terms" element={<LazyPublic><TermsPage /></LazyPublic>} />
                    <Route path="/delete-account" element={<LazyPublic><DeleteAccountPage /></LazyPublic>} />
                    <Route path="/account-deletion" element={<LazyPublic><DeleteAccountPage /></LazyPublic>} />

                    {/* ═══ Dashboard Routes ═══ */}
                    <Route path="/dashboard" element={<AuthenticatedLayout><ProtectedRoute><SmartDashboard /></ProtectedRoute></AuthenticatedLayout>} />
                    <Route path="/user-dashboard" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/admin-dashboard" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/agent-dashboard" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/corporate-dashboard" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/enterprise-dashboard" element={<Navigate to="/dashboard" replace />} />

                    {/* ═══ Admin Sub-Pages ═══ */}
                    <Route path="/admin/applications" element={<LazyAuth role="admin"><AdminApplicationsPage /></LazyAuth>} />
                    <Route path="/admin/users" element={<LazyAuth role="admin"><AdminUsersPage /></LazyAuth>} />
                    <Route path="/admin/reports" element={<LazyAuth role="admin"><AdminReportsPage /></LazyAuth>} />
                    <Route path="/admin/health" element={<LazyAuth role="admin"><AdminHealthPage /></LazyAuth>} />
                    <Route path="/admin/features" element={<LazyAuth role="admin"><AdminFeaturesPage /></LazyAuth>} />
                    <Route path="/admin/logs" element={<LazyAuth role="admin"><AdminLogsPage /></LazyAuth>} />
                    <Route path="/admin/support" element={<LazyAuth role="admin"><AdminSupportPage /></LazyAuth>} />
                    <Route path="/admin/settings" element={<LazyAuth role="admin"><AdminSettingsPage /></LazyAuth>} />
                    <Route path="/admin/analytics" element={<LazyAuth role="admin"><AdminAnalyticsPage /></LazyAuth>} />
                    <Route path="/admin/bulk-operations" element={<LazyAuth role="admin"><AdminBulkOpsPage /></LazyAuth>} />
                    <Route path="/admin/audit-logs" element={<LazyAuth role="admin"><AdminAuditPage /></LazyAuth>} />
                    <Route path="/admin/email-templates" element={<LazyAuth role="admin"><AdminEmailTemplatesPage /></LazyAuth>} />
                    <Route path="/admin/credits" element={<LazyAuth role="admin"><AdminCreditsPage /></LazyAuth>} />

                    {/* ═══ Agent Sub-Pages ═══ */}
                    <Route path="/agent/clients" element={<LazyAuth role="agent"><AgentClientsPage /></LazyAuth>} />
                    <Route path="/agent/clients/:id" element={<LazyAuth role="agent"><AgentClientDetailPage /></LazyAuth>} />
                    <Route path="/agent/new-client" element={<LazyAuth role="agent"><AgentNewClientPage /></LazyAuth>} />
                    <Route path="/agent/applications" element={<LazyAuth role="agent"><AgentApplicationsPage /></LazyAuth>} />
                    <Route path="/agent/commission" element={<LazyAuth role="agent"><AgentCommissionPage /></LazyAuth>} />
                    <Route path="/agent/commission-reports" element={<LazyAuth role="agent"><AgentCommissionReportsPage /></LazyAuth>} />
                    <Route path="/agent/client-portal" element={<LazyAuth role="agent"><AgentClientPortalPage /></LazyAuth>} />
                    <Route path="/agent/payouts" element={<LazyAuth role="agent"><AgentPayoutsPage /></LazyAuth>} />
                    <Route path="/agent/resources" element={<LazyAuth role="agent"><AgentResourcesPage /></LazyAuth>} />
                    <Route path="/agent/settings" element={<LazyAuth role="agent"><AgentSettingsPage /></LazyAuth>} />
                    <Route path="/agent/bulk-submit" element={<LazyAuth role="agent"><AgentBulkSubmitPage /></LazyAuth>} />
                    <Route path="/agent/reports" element={<LazyAuth role="agent"><AgentReportsPage /></LazyAuth>} />

                    {/* ═══ Corporate Sub-Pages ═══ */}
                    <Route path="/corporate/employees" element={<LazyAuth role="corporate"><CorporateEmployeesPage /></LazyAuth>} />
                    <Route path="/corporate/departments" element={<LazyAuth role="corporate"><CorporateDepartmentsPage /></LazyAuth>} />
                    <Route path="/corporate/bulk" element={<LazyAuth role="corporate"><CorporateBulkPage /></LazyAuth>} />
                    <Route path="/corporate/approvals" element={<LazyAuth role="corporate"><CorporateApprovalsPage /></LazyAuth>} />
                    <Route path="/corporate/policies" element={<LazyAuth role="corporate"><CorporatePoliciesPage /></LazyAuth>} />
                    <Route path="/corporate/budgets" element={<LazyAuth role="corporate"><CorporateBudgetsPage /></LazyAuth>} />
                    <Route path="/corporate/reports" element={<LazyAuth role="corporate"><CorporateReportsPage /></LazyAuth>} />

                    {/* ═══ Enterprise Sub-Pages ═══ */}
                    <Route path="/enterprise/sso" element={<LazyAuth role="enterprise"><EnterpriseSSOPage /></LazyAuth>} />
                    <Route path="/enterprise/compliance" element={<LazyAuth role="enterprise"><EnterpriseCompliancePage /></LazyAuth>} />
                    <Route path="/enterprise/sandbox" element={<LazyAuth role="enterprise"><EnterpriseSandboxPage /></LazyAuth>} />
                    <Route path="/enterprise/team" element={<LazyAuth role="enterprise"><EnterpriseTeamPage /></LazyAuth>} />
                    <Route path="/enterprise/budget" element={<LazyAuth role="enterprise"><EnterpriseBudgetPage /></LazyAuth>} />
                    <Route path="/enterprise/travel" element={<LazyAuth role="enterprise"><EnterpriseTravelPage /></LazyAuth>} />
                    <Route path="/enterprise/entities" element={<LazyAuth role="enterprise"><EnterpriseEntitiesPage /></LazyAuth>} />
                    <Route path="/enterprise/workforce" element={<LazyAuth role="enterprise"><EnterpriseWorkforcePage /></LazyAuth>} />
                    <Route path="/enterprise/api" element={<LazyAuth role="enterprise"><EnterpriseAPIPage /></LazyAuth>} />
                    <Route path="/enterprise/whitelabel" element={<LazyAuth role="enterprise"><EnterpriseWhitelabelPage /></LazyAuth>} />
                    <Route path="/enterprise/workflows" element={<LazyAuth role="enterprise"><EnterpriseWorkflowsPage /></LazyAuth>} />
                    <Route path="/enterprise/analytics" element={<LazyAuth role="enterprise"><EnterpriseAnalyticsPage /></LazyAuth>} />

                    {/* ═══ Application Routes ═══ */}
                    <Route path="/application" element={<AuthenticatedLayout><ProtectedRoute><ApplicationPage /></ProtectedRoute></AuthenticatedLayout>} />
                    <Route path="/applications" element={<AuthenticatedLayout><ProtectedRoute><ApplicationsListPage /></ProtectedRoute></AuthenticatedLayout>} />
                    <Route path="/applications/new" element={<AuthenticatedLayout><ProtectedRoute><NewApplicationPage /></ProtectedRoute></AuthenticatedLayout>} />
                    <Route path="/applications/:id" element={<AuthenticatedLayout><ProtectedRoute><ApplicationDetailPage /></ProtectedRoute></AuthenticatedLayout>} />
                    <Route path="/applications/edit/:id" element={<AuthenticatedLayout><ProtectedRoute><ApplicationPage /></ProtectedRoute></AuthenticatedLayout>} />

                    {/* ═══ Visa Routes ═══ */}
                    <Route path="/visa/application/:countryCode/:visaType" element={<AuthenticatedLayout><ProtectedRoute><RedirectVisaApplication /></ProtectedRoute></AuthenticatedLayout>} />

                    {/* ═══ Credits Routes ═══ */}
                    <Route path="/credits" element={<LazyAuth><CreditsPage /></LazyAuth>} />
                    <Route path="/credits/purchase" element={<LazyAuth><CreditsPurchasePage /></LazyAuth>} />
                    <Route path="/credits/success" element={<LazyAuth><CreditsSuccessPage /></LazyAuth>} />
                    <Route path="/credits/history" element={<LazyAuth><CreditsHistoryPage /></LazyAuth>} />
                    <Route path="/credits/promo" element={<LazyAuth><CreditsPromoPage /></LazyAuth>} />

                    {/* ═══ Travel Routes ═══ */}
                    <Route path="/travel/search" element={<LazyAuth><TravelSearchPage /></LazyAuth>} />
                    <Route path="/travel/itinerary" element={<LazyAuth><TravelItineraryPage /></LazyAuth>} />
                    <Route path="/travel-history" element={<LazyAuth><TravelHistoryPage /></LazyAuth>} />
                    <Route path="/travel-intel" element={<LazyAuth><TravelIntelPage /></LazyAuth>} />

                    {/* ═══ Documents Routes ═══ */}
                    <Route path="/documents" element={<LazyAuth><DocumentsPage /></LazyAuth>} />
                    <Route path="/documents/upload" element={<LazyAuth><DocumentsUploadPage /></LazyAuth>} />
                    <Route path="/documents/checklist" element={<LazyAuth><DocumentsChecklistPage /></LazyAuth>} />
                    <Route path="/documents/checklist/:visaKey" element={<LazyAuth><DocumentsChecklistPage /></LazyAuth>} />

                    {/* ═══ Account & Billing Routes ═══ */}
                    <Route path="/payment" element={<AuthenticatedLayout><ProtectedRoute><PaymentPage /></ProtectedRoute></AuthenticatedLayout>} />
                    <Route path="/payments" element={<Navigate to="/payment" replace />} />
                    <Route path="/billing" element={<LazyAuth><BillingPage /></LazyAuth>} />
                    <Route path="/invoices" element={<LazyAuth><InvoicesPage /></LazyAuth>} />
                    <Route path="/profile" element={<AuthenticatedLayout><ProtectedRoute><ProfilePage /></ProtectedRoute></AuthenticatedLayout>} />
                    <Route path="/settings" element={<AuthenticatedLayout><ProtectedRoute><SettingsPage /></ProtectedRoute></AuthenticatedLayout>} />
                    <Route path="/checkout/success" element={<AuthenticatedLayout><ProtectedRoute><CheckoutSuccessPage /></ProtectedRoute></AuthenticatedLayout>} />

                    {/* ═══ Notifications, Drafts, Subscriptions ═══ */}
                    <Route path="/notifications" element={<LazyAuth><NotificationsPage /></LazyAuth>} />
                    <Route path="/drafts" element={<LazyAuth><DraftsPage /></LazyAuth>} />
                    <Route path="/subscriptions" element={<LazyAuth><SubscriptionsPage /></LazyAuth>} />

                    {/* ═══ AI & Intelligence ═══ */}
                    <Route path="/ai-advisor" element={<LazyAuth><AIAdvisorPage /></LazyAuth>} />
                    <Route path="/intelligence" element={<LazyAuth><IntelligencePage /></LazyAuth>} />

                    {/* ═══ Verification & Compliance ═══ */}
                    <Route path="/verification" element={<LazyAuth><VerificationPage /></LazyAuth>} />
                    <Route path="/compliance" element={<LazyAuth><CompliancePage /></LazyAuth>} />

                    {/* ═══ Misc Authenticated Routes ═══ */}
                    <Route path="/bulk-upload" element={<LazyAuth><BulkUploadPage /></LazyAuth>} />
                    <Route path="/reminders" element={<LazyAuth><RemindersPage /></LazyAuth>} />
                    <Route path="/saved" element={<LazyAuth><SavedPage /></LazyAuth>} />
                    <Route path="/country-hub/:countryCode" element={<LazyAuth><CountryHubPage /></LazyAuth>} />
                    <Route path="/interview-prep/:countryCode/:visaType" element={<LazyAuth><InterviewPrepPage /></LazyAuth>} />
                    <Route path="/interview-prep/:countryCode" element={<LazyAuth><InterviewPrepPage /></LazyAuth>} />
                    <Route path="/interview-prep" element={<LazyAuth><InterviewPrepPage /></LazyAuth>} />

                    {/* ═══ Support ═══ */}
                    <Route path="/support" element={<Navigate to="/help" replace />} />

                    {/* ═══ Legacy Redirects (preserve bookmarks & old links) ═══ */}
                    <Route path="/my-applications" element={<Navigate to="/applications" replace />} />
                    <Route path="/application/new" element={<Navigate to="/applications/new" replace />} />
                    <Route path="/applications/:applicationId/edit" element={<RedirectAppEditLegacy />} />
                    <Route path="/visa/check" element={<Navigate to="/visa-check" replace />} />
                    <Route path="/visa/destinations" element={<Navigate to="/countries" replace />} />
                    <Route path="/visa/:countryCode" element={<RedirectVisaCountry />} />
                    <Route path="/visa/application/:id" element={<RedirectVisaAppDetail />} />
                    <Route path="/visa/application/new" element={<Navigate to="/applications/new" replace />} />
                    <Route path="/my-drafts" element={<Navigate to="/drafts" replace />} />
                    <Route path="/tier-subscriptions" element={<Navigate to="/subscriptions" replace />} />
                    <Route path="/billing/invoices" element={<Navigate to="/invoices" replace />} />
                    <Route path="/travel-intelligence" element={<Navigate to="/travel-intel" replace />} />
                    <Route path="/agent/commissions" element={<Navigate to="/agent/commission" replace />} />
                    <Route path="/admin" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/enterprise" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/agent/dashboard" element={<Navigate to="/dashboard" replace />} />

                    {/* ═══ 404 Catch-all ═══ */}
                    <Route path="*" element={<MainLayout><Box sx={{ textAlign: 'center', py: 12 }}><Typography variant="h3" gutterBottom>404</Typography><Typography variant="h6" color="text.secondary" gutterBottom>Page Not Found</Typography><Typography color="text.secondary" sx={{ mb: 3 }}>The page you're looking for doesn't exist or has been moved.</Typography><Button variant="contained" href="/dashboard">Go to Dashboard</Button></Box></MainLayout>} />
                  </Routes>
                  <CookieConsentBanner />
                </div>
              </Router>
            </NotificationProvider>
          </AuthProvider>
        </Elements>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
